import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Typography, message, Modal } from 'antd';
import { CarOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  signInWithEmailPassword,
  supabase,
  parseUserRole,
  requestPasswordReset,
  resetPasswordRedirectUrl,
  PASSWORD_RESET_REQUEST_ACK,
  PASSWORD_RESET_REQUEST_ACK_ZH,
} from '@ubus/shared';
import { homePathForRole } from '../utils/homePath';
import LanguageSwitcher from './LanguageSwitcher';
import { translateAuthError } from '../i18n/translateErrors';

const { Title, Text, Paragraph } = Typography;

export function AdminLoginPage() {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotForm] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate(homePathForRole(parseUserRole(session.user)));
    });
  }, [navigate]);

  const handleLogin = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const { session, error } = await signInWithEmailPassword(values.email, values.password);
      if (error) {
        message.error(translateAuthError(error, t));
      } else if (session) {
        message.success(t('login.success'));
        navigate(homePathForRole(parseUserRole(session.user)));
      }
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('login.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (values: { email: string }) => {
    setForgotLoading(true);
    try {
      await requestPasswordReset(values.email, resetPasswordRedirectUrl());
      message.success(
        i18n.language === 'zh-CN' ? PASSWORD_RESET_REQUEST_ACK_ZH : PASSWORD_RESET_REQUEST_ACK
      );
      setForgotOpen(false);
      forgotForm.resetFields();
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="admin-login">
      <div className="admin-login__lang">
        <LanguageSwitcher />
      </div>
      <div className="admin-login__panel admin-login__panel--brand">
        <div className="admin-login__brand-icon">
          <CarOutlined />
        </div>
        <Title level={2} className="admin-login__brand-title">
          {t('login.brandTitle')}
        </Title>
        <Paragraph className="admin-login__brand-desc">{t('login.brandDesc')}</Paragraph>
        <div className="admin-login__trust">
          <SafetyCertificateOutlined />
          <span>{t('login.trust')}</span>
        </div>
      </div>

      <div className="admin-login__panel admin-login__panel--form">
        <div className="admin-login__form-card">
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('login.formTitle')}
          </Title>
          <Text type="secondary">{t('login.formSubtitle')}</Text>

          <Form
            name="admin-login"
            layout="vertical"
            size="large"
            onFinish={handleLogin}
            style={{ marginTop: 28 }}
            requiredMark={false}
          >
            <Form.Item
              name="email"
              label={t('login.email')}
              rules={[{ required: true, message: t('login.emailRequired') }]}
            >
              <Input placeholder="admin@ubus.com.au" autoComplete="email" />
            </Form.Item>
            <Form.Item
              name="password"
              label={t('login.password')}
              rules={[{ required: true, message: t('login.passwordRequired') }]}
            >
              <Input.Password
                placeholder={t('login.passwordPlaceholder')}
                autoComplete="current-password"
              />
            </Form.Item>
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <Button type="link" style={{ padding: 0 }} onClick={() => setForgotOpen(true)}>
                {t('login.forgotPassword')}
              </Button>
            </div>
            <Form.Item style={{ marginBottom: 8 }}>
              <Button type="primary" htmlType="submit" loading={loading} block>
                {t('login.submit')}
              </Button>
            </Form.Item>
          </Form>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('login.forgotAccountHint')}
          </Text>
          <div style={{ marginTop: 8 }}>
            <Link to="/reset-password" style={{ fontSize: 12 }}>
              {t('login.haveResetLink')}
            </Link>
          </div>
        </div>
      </div>

      <Modal
        title={t('login.resetTitle')}
        open={forgotOpen}
        onCancel={() => setForgotOpen(false)}
        onOk={() => forgotForm.submit()}
        confirmLoading={forgotLoading}
        okText={t('login.sendReset')}
        cancelText={t('common.cancel')}
      >
        <Form form={forgotForm} layout="vertical" onFinish={(v) => void handleForgot(v)}>
          <Form.Item
            name="email"
            label={t('login.email')}
            rules={[{ required: true, type: 'email', message: t('login.emailRequired') }]}
          >
            <Input autoComplete="email" />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('login.resetAntiEnumNote')}
          </Text>
        </Form>
      </Modal>
    </div>
  );
}

export default AdminLoginPage;
